import React, { useState, useEffect, useMemo } from 'react';
import { 
  X, 
  MinusCircle, 
  Plus, 
  Trash2, 
  Loader2, 
  AlertCircle, 
  Calendar, 
  User, 
  FileText 
} from 'lucide-react';
import { useStore, setState } from '../../hooks/useStore.js';
import { groupInventoryByProduct, sortLotsByExpiry } from '../../utils/sorting.js';
import { takeBoxContents } from '../../utils/box-contents.js';
import { updateInventoryItem } from '../../state/store.js';
import { saveLocalData } from '../../state/storage.js';
import { syncAfterLocalChange } from '../../services/sync.js';
import { getAuthenticatedOperatorName, getAuthenticatedOperatorEmail } from '../../services/auth.js';
import { hasCloudSyncConfig } from '../../services/supabase.js';
import { rpcStockOutInventory } from '../../services/rpc.js';

export function StockOutModal({ 
  isOpen, 
  preselectedProductKeys = [], 
  onClose, 
  onSuccess 
}) {
  const inventory = useStore(s => s.inventory) || [];
  const stockOutMaster = useStore(s => s.stockOutMaster) || [];
  const stockOutDetails = useStore(s => s.stockOutDetails) || [];

  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [stockOutDate, setStockOutDate] = useState('');
  const [operator, setOperator] = useState('');
  const [reason, setReason] = useState('');
  const [basket, setBasket] = useState([]); // [{ productKey, quantity }]

  const activeProducts = useMemo(() => {
    const activeItems = inventory.filter(i => !i.deleted);
    return Array.from(groupInventoryByProduct(activeItems).values())
      .filter(g => g.totalQuantity > 0);
  }, [inventory]);

  useEffect(() => {
    if (!isOpen) return;

    const today = new Date().toISOString().split('T')[0];
    setStockOutDate(today);

    const defaultOperator = getAuthenticatedOperatorName() || getAuthenticatedOperatorEmail() || 'เจ้าหน้าที่คลัง SDR';
    setOperator(defaultOperator);
    setReason('');
    setErrorMsg('');

    if (preselectedProductKeys.length > 0) {
      setBasket(preselectedProductKeys.map(k => ({ productKey: k, quantity: 1 })));
    } else if (activeProducts.length > 0) {
      setBasket([{ productKey: activeProducts[0].key, quantity: 1 }]);
    } else {
      setBasket([]);
    }
  }, [isOpen, preselectedProductKeys, activeProducts]);

  if (!isOpen) return null;

  const handleAddRow = () => {
    if (activeProducts.length === 0) return;
    setBasket(prev => [...prev, { productKey: activeProducts[0].key, quantity: 1 }]);
  };

  const handleRemoveRow = (index) => {
    setBasket(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateRow = (index, field, value) => {
    setBasket(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (basket.length === 0) {
      setErrorMsg('กรุณาเลือกรายการพัสดุที่ต้องการจ่ายออก');
      return;
    }

    if (!reason.trim()) {
      setErrorMsg('กรุณาระบุเหตุผลการจ่ายออก');
      return;
    }

    setSaving(true);
    setErrorMsg('');

    try {
      const nowIso = new Date().toISOString();
      const operatorEmail = getAuthenticatedOperatorEmail() || 'user';
      const masterId = `so_master_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;

      const newDetails = [];
      const updatedLots = [];

      for (let i = 0; i < basket.length; i++) {
        const itemReq = basket[i];
        const reqQty = Number(itemReq.quantity) || 0;
        if (reqQty <= 0) continue;

        const product = activeProducts.find(p => p.key === itemReq.productKey);
        if (!product) continue;

        if (reqQty > product.totalQuantity) {
          throw new Error(`ยอดตัดจ่ายของ "${product.name}" (${reqQty}) เกินจำนวนคงเหลือที่มี (${product.totalQuantity})`);
        }

        // Local FEFO deduction across lots
        let remainingToDeduct = reqQty;
        const sortedLots = sortLotsByExpiry(product.lots || []);

        for (let l = 0; l < sortedLots.length; l++) {
          if (remainingToDeduct <= 0) break;
          const lot = sortedLots[l];
          const availableInLot = Number(lot.quantity) || 0;
          if (availableInLot <= 0) continue;

          const deductFromThisLot = Math.min(availableInLot, remainingToDeduct);
          const newLotQty = Number((availableInLot - deductFromThisLot).toFixed(5));

          let newBoxContents = lot.boxContents;
          if (Array.isArray(lot.boxContents) && lot.boxContents.length > 0) {
            newBoxContents = takeBoxContents(lot.boxContents, deductFromThisLot);
          }

          newDetails.push({
            id: `so_detail_${Date.now()}_${Math.random().toString(36).substring(2, 8)}_${i}_${l}`,
            stockOutId: masterId,
            masterId: masterId,
            itemId: lot.id,
            itemName: product.name,
            brand: product.brand || '',
            size: product.size || '',
            lot: lot.lot || 'ล็อตหลัก',
            quantity: deductFromThisLot,
            unit: product.unit || 'ชิ้น',
            deleted: false,
            updatedAt: nowIso
          });

          updatedLots.push({
            ...lot,
            quantity: newLotQty,
            boxContents: newBoxContents,
            updatedAt: nowIso,
            updatedBy: operatorEmail
          });

          remainingToDeduct -= deductFromThisLot;
        }
      }

      if (newDetails.length === 0) {
        throw new Error('ไม่พบรายการพัสดุที่สามารถตัดจ่ายได้');
      }

      // Online RPC attempt
      if (hasCloudSyncConfig()) {
        try {
          await rpcStockOutInventory({
            id: masterId,
            operatorName: operator.trim(),
            reason: reason.trim(),
            items: newDetails.map(d => ({
              itemId: d.itemId,
              itemName: d.itemName,
              quantity: d.quantity,
              unit: d.unit
            })),
            timestamp: nowIso,
            operatorEmail: operatorEmail
          });
        } catch (rpcErr) {
          console.warn('RPC stock out fallback to local transaction:', rpcErr);
        }
      }

      // Apply lot updates
      updatedLots.forEach(lot => {
        updateInventoryItem(lot);
      });

      // Add Stock Out Master record
      const dateStr = stockOutDate || nowIso.split('T')[0];
      const masterRecord = {
        id: masterId,
        timestamp: nowIso,
        date: dateStr,
        createdAt: nowIso,
        updatedAt: nowIso,
        operator: operator.trim(),
        operatorName: operator.trim(),
        operatorEmail: operatorEmail,
        reason: reason.trim(),
        deleted: false
      };

      setState({
        stockOutMaster: [masterRecord, ...stockOutMaster],
        stockOutDetails: [...newDetails, ...stockOutDetails]
      });

      saveLocalData();
      syncAfterLocalChange('stock-out-mutation');

      onClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      setErrorMsg(err.message || 'เกิดข้อผิดพลาดในการบันทึกการจ่ายออก');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-150 overflow-y-auto">
      <div className="relative w-full max-w-xl rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-150 my-6">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-red-50/50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center text-red-700">
              <MinusCircle className="w-4 h-4" />
            </div>
            <h2 className="text-base font-bold text-slate-900">บันทึกการจ่ายสินค้าออก (Stock Out)</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
          {errorMsg && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Date & Operator */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-800 mb-1">
                วันที่จ่ายออก <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="date"
                  required
                  value={stockOutDate}
                  onChange={(e) => setStockOutDate(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-slate-300 bg-white text-slate-900 font-medium focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-800 mb-1">
                ผู้ดำเนินการจ่ายออก <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  required
                  value={operator}
                  onChange={(e) => setOperator(e.target.value)}
                  placeholder="ชื่อผู้ดำเนินการ"
                  className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-slate-300 bg-white text-slate-900 font-medium placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-800 mb-1">
              เหตุผลการจ่ายออก / ผู้รับมอบ <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <FileText className="w-4 h-4 absolute left-3 top-3 text-slate-500" />
              <textarea
                rows={2}
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="เช่น เบิกไปใช้ในห้องแล็บ, จำหน่ายพัสดุเสื่อมสภาพ, มอบให้คณะกรรมการ..."
                className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-slate-300 bg-white text-slate-900 font-medium placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
              />
            </div>
          </div>

          {/* Basket */}
          <div className="pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                รายการพัสดุและจำนวนที่จ่ายออก ({basket.length} รายการ)
              </span>
              <button
                type="button"
                onClick={handleAddRow}
                className="text-xs text-red-600 hover:text-red-800 font-semibold flex items-center gap-1 cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>เพิ่มพัสดุ</span>
              </button>
            </div>

            <div className="space-y-2">
              {basket.map((row, idx) => {
                const selectedProd = activeProducts.find(p => p.key === row.productKey);
                return (
                  <div key={idx} className="flex items-center gap-2 p-2.5 rounded-xl border border-slate-200 bg-slate-50/70">
                    <select
                      value={row.productKey}
                      onChange={(e) => handleUpdateRow(idx, 'productKey', e.target.value)}
                      className="flex-1 px-2.5 py-1.5 text-xs rounded-lg border border-slate-200 bg-white font-medium"
                    >
                      {activeProducts.map(p => (
                        <option key={p.key} value={p.key}>
                          {p.name} {p.brand ? `(${p.brand})` : ''} - คงเหลือ {p.totalQuantity} {p.unit}
                        </option>
                      ))}
                    </select>

                    <input
                      type="number"
                      min="1"
                      max={selectedProd ? selectedProd.totalQuantity : 9999}
                      value={row.quantity}
                      onChange={(e) => handleUpdateRow(idx, 'quantity', e.target.value)}
                      className="w-20 px-2 py-1.5 text-xs rounded-lg border border-slate-200 bg-white font-bold text-center"
                    />

                    <span className="text-xs text-slate-500 w-12 truncate">
                      {selectedProd?.unit || 'ชิ้น'}
                    </span>

                    {basket.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveRow(idx)}
                        className="text-slate-400 hover:text-red-500 p-1 cursor-pointer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex items-center justify-end gap-2.5 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold text-xs transition-colors cursor-pointer"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold text-xs shadow-xs transition-colors cursor-pointer"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              <span>{saving ? 'กำลังประมวลผล...' : 'ยืนยันการจ่ายออก'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
